import {
    AgentStateModule,
    BridgeAgent,
    Agent,
    AgentMetadata,
    CallState,
} from '@/store/modules/agent/types';
import { QueueGetters } from '@/store/getters';
import capitalize from '@/utils/text';
import idealName from '@/utils/ideal-name';
import { getUserInfo } from '@/store/plugins/userInfoPlugin';

const Getters = {
    getAgentByName: (state: AgentStateModule) => (name: string): Agent => state.agentsContainer[name] ?? {} as Agent,
    isAgentAttachedToQueue: (state: AgentStateModule) => (agentName: string, queue: string) => {
        const agent = state.agentsContainer[agentName];
        return agent?.tiers.findIndex((el) => el.queue === queue) > -1;
    },
    getFirstQueueCallIdByAgent: (state: AgentStateModule) => (name: string): string => {
        const agent = state.agentsContainer[name];
        const queueCall: BridgeAgent = agent.queueCalls.values().next().value;
        if (queueCall) {
            return queueCall.call;
        }
        return '';
    },
    getFirstCallIdByAgent: (state: AgentStateModule) => (name: string): string => {
        const agent = state.agentsContainer[name];
        const callState: CallState = agent.callStates.values().next().value;
        const queueCall: BridgeAgent = agent.queueCalls.values().next().value;

        if (callState) return callState.call_uuid;

        if (queueCall) return queueCall.call;

        return '';
    },

    agentMetadata: (state: AgentStateModule) => (agentName: string): AgentMetadata => {
        const agent: Agent = state.agentsContainer[agentName];
        return agent?.metadata ?? {};
    },
    agentCallStates: (state: AgentStateModule, getters: QueueGetters) => (agentName: string): Array<CallState> => {
        const agent: Agent = state.agentsContainer[agentName];

        if (!agent) return [];

        const callStateValues = Array.from(agent.callStates.values());

        const filteredCallStates = callStateValues.filter((callState: CallState) => {
            const isInQueueCalls = agent.queueCalls.has(callState.uuid);

            // Si la llamada ya está en queueCalls, no la incluimos para evitar duplicados
            if (isInQueueCalls) {
                return false;
            }

            // Si es una llamada entrante timbrando, la incluimos siempre
            if (callState.state === 'ringing' && callState.direction === 'inbound') {
                const isRingingInboundInQueue = getters.queueNames().some((queue: string) => {
                    const isInQueueOffer = getters.queueOfferAgents(queue).has(callState.agent);
                    return isInQueueOffer;
                });
                return true;
            }

            return true;
        });

        return filteredCallStates;
    },
    agentName: (state: AgentStateModule) => (agentName: string): string => {
        const agent: Agent = state.agentsContainer[agentName];

        const extensionMatch = agentName.match(/^(\d+)@/);
        if (extensionMatch) {
            const extension = extensionMatch[1];
            const userInfo = getUserInfo();

            if (userInfo && userInfo.users && userInfo.users[extension]) {
                return `${extension} - ${capitalize(userInfo.users[extension])}`;
            }
        }

        return agent?.metadata.name || capitalize(idealName(agentName));
    },
    agentExtensionName: () => (agentName: string): string => {
        const extensionMatch = agentName.match(/^(\d+)@/);
        if (extensionMatch) {
            return extensionMatch[1];
        }

        return capitalize(idealName(agentName));
    },
    agentConnected: (state: AgentStateModule) => (agentName: string): boolean => {
        const agent: Agent = state.agentsContainer[agentName];
        return agent.connected;
    },
    isAgentBusy: (state: AgentStateModule) => (agentName: string): boolean => {
        const agent: Agent = state.agentsContainer[agentName];
        return agent.queueCalls.size > 0 || agent.callStates.size > 0;
    },
    existsAgent: (state: AgentStateModule) => (agentName: string): boolean => {
        const agent: Agent = state.agentsContainer[agentName];
        return agent !== undefined;
    },
};
export default Getters;
